import type { LogLevel } from '@core/core/types'

class ConsoleSpy {
  private listeners: Partial<Record<LogLevel, ((...data: unknown[]) => void)[]>> = {}
  public originalConsole: Console
  public spyConsole: Console

  constructor() {
    this.originalConsole = window.console
    this.spyConsole = this.consoleProxyBuilder()
  }

  public enable() {
    window.console = this.spyConsole
  }

  public disable() {
    window.console = this.originalConsole
  }

  public addConsoleListener = (
    methodName: LogLevel,
    callback: (...data: unknown[]) => void,
  ): void => {
    ;(this.listeners[methodName] ??= []).push(callback)
  }

  public removeConsoleListener(methodName: LogLevel, callback?: (...data: unknown[]) => void) {
    this.listeners[methodName] =
      callback === undefined
        ? []
        : this.listeners[methodName]?.filter(listener => listener !== callback)
  }

  private consoleProxyBuilder() {
    const handler: ProxyHandler<Console> = {
      get: (target, methodName: keyof Console) => {
        switch (methodName) {
          case 'log':
          case 'warn':
          case 'error': {
            const targetFn = target[methodName]
            const listenersForTarget = this.listeners[methodName]
            return (...args: unknown[]) => {
              listenersForTarget?.forEach(listener => {
                listener(...args)
              })
              targetFn(...args)
            }
          }
          default:
            return Reflect.get(target, methodName)
        }
      },
    }
    return new Proxy(this.originalConsole, handler)
  }
}

export const consoleSpy = new ConsoleSpy()
